import { useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import {
  AlertTriangle,
  CheckCircle2,
  Database as DatabaseIcon,
  Filter,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
} from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Skeleton } from "@fleet/ui";
import { Select, type SelectOption } from "@/components/ui/select";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { cn, type components, formatBytes, getAdminStorageHandlers } from "@bodega/shared";
import { showToast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { QueryErrorState } from "@/components/ui/query-error-state";

const SKELETON_COUNT = 5;

export function StorageManager() {
  const { t } = useI18n();
  // `pruneOrphanedObjects` is a POST and DELETES S3 objects with no way back; the
  // rest of the panel (listS3Objects + refresh) uses GET and remains open to any
  // back-office role.
  const { can } = useAdminCapabilities();
  const canWrite = () => can("content.write");
  // URL-addressable filter (#15, `?filter=`): pasting the URL reproduces the filter.
  const urlSearch = useSearch({ from: "/_app/admin/storage" });
  const navigate = useNavigate();
  const filter = () => urlSearch().filter ?? "all";
  const setFilter = (f: "all" | "orphaned" | "linked") => {
    void navigate({
      to: "/admin/storage",
      search: (prev) => ({
        ...prev,
        filter: f === "all" ? undefined : f,
      }),
      replace: true,
    });
  };

  const objectsQuery = createApiQuery<
    components["schemas"]["S3ObjectDTO"][],
    components["schemas"]["ErrorModel"]
  >(
    () => ["admin", "s3-objects"],
    async () => api.GET("/api/admin/font/listS3Objects", {}),
    () => ({ staleTime: 0 }),
  );

  const pruneMutation = createApiMutation<
    undefined,
    components["schemas"]["PruneResult"],
    components["schemas"]["ErrorModel"]
  >(async () => api.POST("/api/admin/font/pruneOrphanedObjects"));

  const handlers = createMemo(() =>
    getAdminStorageHandlers({
      objects: objectsQuery.isSuccess ? (objectsQuery.data ?? []) : [],
      onRefetch: () => {
        void objectsQuery.refetch();
      },
      onPrune: () => {
        // This is handled by the ConfirmDialog wrapper in the UI
      },
    }),
  );

  const stats = () => handlers().getStats();
  const filteredObjects = () => handlers().getFilteredObjects(filter());

  type StorageFilter = "all" | "linked" | "orphaned";
  const filterOptions = (): SelectOption<StorageFilter>[] => [
    { value: "all", label: `${t(msg`All`)} (${stats().getLabelCount("all")})` },
    { value: "linked", label: `${t(msg`Linked`)} (${stats().getLabelCount("linked")})` },
    { value: "orphaned", label: `${t(msg`Orphaned`)} (${stats().getLabelCount("orphaned")})` },
  ];

  return (
    <div class="space-y-8">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <DatabaseIcon class="h-8 w-8 text-primary" />
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`S3 Storage`)}</h2>
        </div>
        <div class="flex gap-2">
          <Button
            variant="outline"
            onClick={() => void objectsQuery.refetch()}
            disabled={objectsQuery.isLoading}
          >
            <RefreshCw class={cn("mr-2 h-4 w-4", objectsQuery.isLoading && "animate-spin")} />
            {t(msg`Refresh`)}
          </Button>
          {/* Se OCULTA (no se deshabilita) sin `content.write`: un botón
              destructivo apagado no informa de nada —el contador de huérfanos ya
              está arriba— y sólo invita a probarlo. */}
          <Show when={canWrite()}>
            <ConfirmDialog
              title={t(msg`Clean orphaned objects?`)}
              description={t(
                msg`Are you sure you want to delete orphaned objects? This action cannot be undone and will free up space in S3.`,
              )}
              confirmLabel={t(msg`Clean`)}
              variant="destructive"
              onConfirm={() => {
                pruneMutation.mutate(undefined, {
                  onSuccess: (result) => {
                    const { deleted } = result;
                    showToast(t(msg`Successfully deleted ${deleted} orphaned objects.`), "success");
                    void objectsQuery.refetch();
                  },
                });
              }}
              renderTrigger={(open) => (
                <Button
                  variant="destructive"
                  onClick={open}
                  disabled={pruneMutation.isPending || stats().orphanedCount === 0}
                >
                  <Show when={pruneMutation.isPending} fallback={<Trash2 class="mr-2 h-4 w-4" />}>
                    <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                  </Show>
                  {t(msg`Clean Orphans`)} ({stats().orphanedCount})
                </Button>
              )}
            />
          </Show>
        </div>
      </div>

      <div class="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent class="pt-6 text-center">
            <p class="text-3xl font-bold">{stats().totalCount}</p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Total Objects`)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="pt-6 text-center">
            <p
              class={cn(
                "text-3xl font-bold",
                stats().orphanedCount > 0 ? "text-amber-500" : "text-success",
              )}
            >
              {stats().orphanedCount}
            </p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Orphans`)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent class="pt-6 text-center">
            <p class="text-3xl font-bold">{formatBytes(stats().totalSize)}</p>
            <p class="text-xs text-muted-foreground uppercase tracking-wider mt-1">
              {t(msg`Total Space`)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-2">
          <Filter class="h-4 w-4 shrink-0 text-muted-foreground" />
          <Select<StorageFilter>
            class="max-w-[220px]"
            aria-label={t(msg`Filter objects`)}
            value={filter()}
            onChange={setFilter}
            options={filterOptions()}
          />
        </div>

        <Show when={objectsQuery.isError}>
          <QueryErrorState
            testId="admin-storage-error"
            compact
            onRetry={() => void objectsQuery.refetch()}
          />
        </Show>
        <Show when={!objectsQuery.isError}>
          <Show
            when={!objectsQuery.isLoading}
            fallback={
              <div class="grid gap-3">
                <For each={Array.from({ length: SKELETON_COUNT })}>
                  {() => <Skeleton class="h-16 w-full rounded-lg" />}
                </For>
              </div>
            }
          >
            <div class="grid gap-3">
              <For
                each={filteredObjects()}
                fallback={
                  <div class="py-20 text-center text-muted-foreground">
                    <Search class="mx-auto h-12 w-12 opacity-10 mb-4" />
                    <p>{t(msg`No objects matching the filter.`)}</p>
                  </div>
                }
              >
                {(obj) => (
                  <div
                    class={cn(
                      "flex flex-col gap-1 rounded-lg border p-4 transition-colors",
                      obj.isOrphaned
                        ? "border-amber-500/20 bg-amber-500/5"
                        : "border-border hover:bg-muted/50",
                    )}
                  >
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-2 min-w-0">
                        <Show
                          when={obj.isOrphaned}
                          fallback={<CheckCircle2 class="h-4 w-4 text-success shrink-0" />}
                        >
                          <AlertTriangle class="h-4 w-4 text-amber-500 shrink-0" />
                        </Show>
                        <span class="font-mono text-xs truncate">{obj.filename}</span>
                      </div>
                      <div class="flex gap-1 shrink-0">
                        <Badge variant="outline" class="text-[9px] uppercase">
                          {obj.type}
                        </Badge>
                        <Show when={obj.isOrphaned}>
                          <Badge
                            variant="outline"
                            class="text-[9px] uppercase bg-amber-500/10 text-amber-600 border-amber-500/20"
                          >
                            {t(msg`Orphaned`)}
                          </Badge>
                        </Show>
                      </div>
                    </div>
                    <div class="flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span>{formatBytes(obj.size)}</span>
                      <Show when={obj.lastModified}>
                        <span>• {new Date(obj.lastModified).toLocaleDateString()}</span>
                      </Show>
                      <Show when={!obj.isOrphaned}>
                        <span class="text-success">• {obj.fontFamilyName}</span>
                      </Show>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </div>
    </div>
  );
}
